/**
 * @lines98/client/screens/oauth-callback-screen — OAuth callback page (T-6 client).
 *
 * Renders при загрузке URL `/oauth/google/callback?code=...&state=...` (или
 * /discord/). Pipeline:
 *   1. Mount «обрабатываем вход…» placeholder.
 *   2. Call `handleCallback({ provider, searchString })` from services/oauth-callback.
 *   3. On success: «Вы вошли» + auto-redirect к `/` после 2 sec
 *      (или click «продолжить»).
 *   4. On error: показать reason + кнопка «вернуться» к `/`.
 *
 * Все видимые игроку строки — через `lib/i18n` (`auth.oauth.*`, ru/en); RU-литералов
 * в разметке нет (fe-overlays П2 i18n). Сообщение успеха `result.message` приходит от
 * сервера (server-truth) и не локализуется клиентом.
 *
 * No board screen logic — this is а standalone page rendered before app.ts
 * mounts the game. Selected by `main.ts` if `window.location.pathname` matches
 * the callback pattern.
 *
 * Per ADR-0001 §A2 vanilla TS — no React Router. main.ts dispatches based on
 * `window.location.pathname` before reaching `mountApp()`.
 *
 * Cross-references:
 *   - services/oauth-callback.ts (handleCallback + provider extraction).
 *   - services/oauth-login.ts (sessionStorage keys).
 *   - ADR-0006 §D7 (server-side flow this page completes).
 */

import { t } from '../lib/i18n/index.js';
import {
  handleCallback,
  type CallbackResult,
} from '../services/oauth-callback.js';
import type { OAuthProvider } from '../services/oauth-login.js';

const AUTO_REDIRECT_DELAY_MS = 2000;

/**
 * Mount the OAuth callback screen. Called by main.ts when pathname matches.
 *
 * Side effects:
 *   - Innerly clears `host.innerHTML` then appends рендеренный root.
 *   - Triggers handleCallback() — async — and re-renders когда finishes.
 *   - On success, schedules `window.location.assign('/')` after delay.
 *
 * @returns void (no unmount handle — this screen is terminal: either user
 *          clicks to navigate, or auto-redirect fires).
 */
export function mountOAuthCallbackScreen(args: {
  host: HTMLElement;
  provider: OAuthProvider;
  searchString: string;
}): void {
  const { host, provider, searchString } = args;
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css). Этот
  // экран монтируется напрямую в #app (path-route в main.ts, минуя nav-rail) — класс
  // на root даёт ему тот же edge-клиренс, что и in-shell экранам.
  root.className = 'oauth-callback-screen screen-root';
  root.innerHTML = renderPending(provider);
  host.appendChild(root);

  // Fire async flow; re-render когда complete.
  void (async () => {
    let result: CallbackResult;
    try {
      result = await handleCallback({ provider, searchString });
    } catch (err) {
      result = {
        ok: false,
        reason: 'server_error',
        detail: t('auth.oauth.errCallbackFailed', { message: (err as Error).message }),
      };
    }
    root.innerHTML = result.ok ? renderSuccess(result) : renderError(result);
    wireButtons(root);

    if (result.ok) {
      window.setTimeout(() => {
        // Use assign() so back-button can return to callback page (debug).
        // location.href would also work; assign is more explicit.
        window.location.assign('/');
      }, AUTO_REDIRECT_DELAY_MS);
    }
  })();
}

// =============================================================================
// Rendering
// =============================================================================

function renderPending(provider: OAuthProvider): string {
  const label = providerLabel(provider);
  return `
    <div class="oauth-callback-card">
      <div class="oauth-callback-title screen-title">${escapeHtml(
        t('auth.oauth.pendingTitle', { provider: label }),
      )}</div>
      <div class="oauth-callback-sub">${escapeHtml(t('auth.oauth.pendingSub'))}</div>
    </div>
  `;
}

function renderSuccess(
  result: Extract<CallbackResult, { ok: true }>,
): string {
  const heading =
    result.status === 'first_bind'
      ? t('auth.oauth.successFirstBind')
      : t('auth.oauth.successExisting');
  return `
    <div class="oauth-callback-card">
      <div class="oauth-callback-title screen-title">${escapeHtml(heading)}</div>
      <div class="oauth-callback-sub">${escapeHtml(result.message)}</div>
      <div class="oauth-callback-actions">
        <button class="btn primary" id="btn-continue" type="button">${escapeHtml(
          t('auth.oauth.continue'),
        )}</button>
      </div>
      <div class="oauth-callback-note">${escapeHtml(t('auth.oauth.redirectNote'))}</div>
    </div>
  `;
}

function renderError(
  result: Extract<CallbackResult, { ok: false }>,
): string {
  return `
    <div class="oauth-callback-card oauth-callback-card--error">
      <div class="oauth-callback-title screen-title">${escapeHtml(
        t('auth.oauth.errorTitle'),
      )}</div>
      <div class="oauth-callback-sub">${escapeHtml(result.detail)}</div>
      <div class="oauth-callback-actions">
        <button class="btn primary" id="btn-back" type="button">${escapeHtml(
          t('auth.oauth.back'),
        )}</button>
      </div>
    </div>
  `;
}

function wireButtons(root: HTMLElement): void {
  const continueBtn = root.querySelector<HTMLButtonElement>('#btn-continue');
  continueBtn?.addEventListener('click', () => {
    window.location.assign('/');
  });
  const backBtn = root.querySelector<HTMLButtonElement>('#btn-back');
  backBtn?.addEventListener('click', () => {
    window.location.assign('/');
  });
}

// =============================================================================
// Helpers
// =============================================================================

function providerLabel(provider: OAuthProvider): string {
  return provider === 'google' ? 'Google' : 'Discord';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&'
      ? '&amp;'
      : ch === '<'
        ? '&lt;'
        : ch === '>'
          ? '&gt;'
          : '&quot;',
  );
}
